package floris.graphics.repo

import floris.graphics.NetworkingConfig
import floris.graphics.QueryOptions
import floris.graphics.deleteRequest
import floris.graphics.extensions.buildFormData
import floris.graphics.getRequest
import floris.graphics.models.NetworkResponse
import floris.graphics.models.domain.models.project.Project
import floris.graphics.models.domain.models.project.ProjectDetail
import floris.graphics.models.domain.models.project.toProject
import floris.graphics.models.domain.models.project.toProjectDetail
import floris.graphics.models.dto.requests.project.InsertProject
import floris.graphics.models.dto.requests.project.ProjectDetailDto
import floris.graphics.models.dto.requests.project.ProjectDto
import floris.graphics.models.dto.requests.project.UpdateProject
import floris.graphics.models.dto.responses.ErrorResponse
import floris.graphics.postRequest
import floris.graphics.putRequest
import org.w3c.dom.url.URLSearchParams
import org.w3c.files.File

object ProjectRepo {
    private val projectsUrl = "${NetworkingConfig.baseUrl}/api/v1/projects"

    suspend fun getProjects(query: String? = null, categoryId: String? = null, queryOptions: QueryOptions = QueryOptions()): List<Project> {
        val params = URLSearchParams().apply {
            if (!query.isNullOrBlank()) append("query", query)
            if (!categoryId.isNullOrBlank()) append("category_id", categoryId)
        }

        val url = "$projectsUrl/?${params}"

        val response: NetworkResponse<List<ProjectDto>, ErrorResponse> = getRequest(url, queryOptions, true)

        return when (response) {
            is NetworkResponse.Success -> response.body.map { it.toProject() }
            else -> emptyList()
        }
    }

    suspend fun getProject(projectId: String): ProjectDetail? {
        val url = "$projectsUrl/$projectId"

        val response: NetworkResponse<ProjectDetailDto, ErrorResponse> = getRequest(url, allowUnauthenticated = true)

        return when (response) {
            is NetworkResponse.Success -> response.body.toProjectDetail()
            else -> null
        }
    }

    suspend fun getNextProjectId(projectId: String): String? {
        val url = "$projectsUrl/$projectId/next_id"

        val response: NetworkResponse<String, ErrorResponse> = getRequest(url)

        return when (response) {
            is NetworkResponse.Success -> response.body
            else -> null
        }
    }

    suspend fun getPreviousProjectId(projectId: String): String? {
        val url = "$projectsUrl/$projectId/previous_id"

        val response: NetworkResponse<String, ErrorResponse> = getRequest(url)

        return when (response) {
            is NetworkResponse.Success -> response.body
            else -> null
        }
    }

    suspend fun createProject(project: InsertProject, logo: File? = null, previewVideo: File? = null, frameMedia: List<File> = emptyList()): ProjectDetail? {
        val url = projectsUrl

        val formData = buildFormData(
            data = project,
            extraFields = mapOf(
                "thumbnail" to logo,
                "preview_video" to previewVideo
            ).plus(
                frameMedia.mapIndexed { index, file ->
                    "frame_media[$index]" to file
                }
            ).filterValues { it != null }
        )

        val response: NetworkResponse<ProjectDetailDto?, ErrorResponse> = postRequest(
            resource = url,
            body = formData
        )

        return when (response) {
            is NetworkResponse.Success -> response.body?.toProjectDetail()
            is NetworkResponse.ServerError -> throw RuntimeException(response.body?.errorDescription)
            is NetworkResponse.Error -> throw response.error ?: Exception("Unknown error")
        }
    }

    suspend fun updateProject(projectId: String, project: UpdateProject): ProjectDetail? {
        val url = "$projectsUrl/$projectId"
        val response: NetworkResponse<ProjectDetailDto, ErrorResponse> = putRequest(resource = url, body = project)

        return when (response) {
            is NetworkResponse.Success -> response.body.toProjectDetail()
            else -> null
        }
    }

    suspend fun updateProjectImage(
        projectId: String,
        logo: File? = null,
        removeThumbnail: Boolean = false,
        previewVideo: File? = null,
        frameMedia: List<File> = emptyList()
    ): ProjectDetail? {
        val url = "$projectsUrl/$projectId/upload"

        val formData = buildFormData(
            data = mapOf("remove_thumbnail" to removeThumbnail.toString()),
            extraFields = mapOf(
                "thumbnail" to logo,
                "preview_video" to previewVideo
            ).plus(
                frameMedia.mapIndexed { index, file ->
                    "frame_media[$index]" to file
                }
            ).filterValues { it != null }
        )

        val response: NetworkResponse<ProjectDetailDto?, ErrorResponse> = putRequest(
            resource = url,
            body = formData
        )

        return when (response) {
            is NetworkResponse.Success -> response.body?.toProjectDetail()
            is NetworkResponse.ServerError -> throw RuntimeException(response.body?.errorDescription)
            is NetworkResponse.Error -> throw response.error ?: Exception("Unknown error")
        }
    }

    suspend fun deleteProject(projectId: String): Boolean {
        val url = "$projectsUrl/$projectId"

        val response = deleteRequest(url)

        return when (response) {
            is NetworkResponse.Success -> true
            else -> false
        }
    }

    suspend fun deleteProjectMediaById(projectId: String, mediaId: String): Boolean {
        val url = "$projectsUrl/$projectId/media/$mediaId"

        val response = deleteRequest(url)

        return when (response) {
            is NetworkResponse.Success -> true
            else -> false
        }
    }
}
